import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';

const GlowingSphere = ({ position, scale, color, speed, wireframe = true }) => {
  const meshRef = useRef();

  useFrame(({ clock }) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = clock.getElapsedTime() * speed * 0.3;
      meshRef.current.rotation.y = clock.getElapsedTime() * speed * 0.2;
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.4} floatIntensity={2}>
      <mesh ref={meshRef} position={position} scale={scale}>
        <icosahedronGeometry args={[1, wireframe ? 1 : 2]} />
        <meshStandardMaterial
          color={color}
          wireframe={wireframe}
          transparent
          opacity={wireframe ? 0.4 : 0.15}
        />
      </mesh>
    </Float>
  );
};

const ParticleField = () => {
  const pointsRef = useRef();
  const count = 300;

  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 10;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 10;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    return pos;
  }, []);

  useFrame(({ clock }) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = clock.getElapsedTime() * 0.03;
      pointsRef.current.rotation.x = Math.sin(clock.getElapsedTime() * 0.02) * 0.15;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.025}
        color="#a31d22"
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </points>
  );
};

const OrbitalRing = ({ radius, tube, position, speed = 0.15 }) => {
  const meshRef = useRef();

  useFrame(({ clock }) => {
    if (meshRef.current) {
      meshRef.current.rotation.x = clock.getElapsedTime() * speed;
      meshRef.current.rotation.z = clock.getElapsedTime() * speed * 0.6;
    }
  });

  return (
    <Float speed={0.8} rotationIntensity={0.15} floatIntensity={1}>
      <mesh ref={meshRef} position={position}>
        <torusGeometry args={[radius, tube, 16, 100]} />
        <meshStandardMaterial
          color="#a31d22"
          wireframe
          transparent
          opacity={0.18}
        />
      </mesh>
    </Float>
  );
};

const ConnectionLines = () => {
  const lineRef = useRef();

  const geometry = useMemo(() => {
    const points = [];
    for (let i = 0; i < 8; i++) {
      points.push(
        new THREE.Vector3(
          (Math.random() - 0.5) * 6,
          (Math.random() - 0.5) * 6,
          (Math.random() - 0.5) * 4
        )
      );
    }
    return new THREE.BufferGeometry().setFromPoints(points);
  }, []);

  useFrame(({ clock }) => {
    if (lineRef.current) {
      lineRef.current.rotation.y = clock.getElapsedTime() * 0.02;
    }
  });

  return (
    <line ref={lineRef} geometry={geometry}>
      <lineBasicMaterial color="#a31d22" transparent opacity={0.08} />
    </line>
  );
};

const Scene3D = () => {
  return (
    <div className="absolute inset-0 pointer-events-none">
      <Canvas
        camera={{ position: [0, 0, 5.5], fov: 60 }}
        style={{ background: 'transparent' }}
        gl={{ alpha: true, antialias: true }}
      >
        <ambientLight intensity={0.7} />
        <directionalLight position={[5, 5, 5]} intensity={1} />
        <pointLight position={[-3, 2, 3]} intensity={0.5} color="#a31d22" />

        <GlowingSphere position={[2.5, 0.5, 0]} scale={1.4} color="#a31d22" speed={0.4} />
        {/* <GlowingSphere position={[-2.5, -1.5, -1.5]} scale={0.7} color="#a31d22" speed={0.6} /> */}
        <GlowingSphere position={[0.5, -2, -1]} scale={0.45} color="#190107" speed={0.8} />
        <GlowingSphere position={[-1, 2, -2]} scale={0.3} color="#a31d22" speed={1} wireframe={false} />

        <OrbitalRing radius={2.2} tube={0.015} position={[2, 0, -0.5]} speed={0.12} />
        {/* <OrbitalRing radius={1.3} tube={0.01} position={[-2, 1, -1.5]} speed={0.18} /> */}
        <OrbitalRing radius={0.8} tube={0.008} position={[0.5, -1.5, -1]} speed={0.25} />

        <ConnectionLines />
        <ParticleField />
      </Canvas>
    </div>
  );
};

export default Scene3D;
